import { Component, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { EndRidePhotos, Ride } from '../../../models/ride';
import { DurationPipe } from '../../../@pipes/duration-pipe';
import { CarService } from '../../../services/vehicles/car-service';
import { Car } from '../../../models/car';
import { CustomerService } from '../../../services/customers/customer-service';
import { Customer } from '../../../models/customer';

interface PhotoEntry { label: string; url: string; }

@Component({
  selector: 'app-ride-verification-details',
  imports: [CommonModule, DurationPipe, RouterLink],
  providers: [DatePipe],
  templateUrl: './ride-verification-details.html',
  styleUrl: './ride-verification-details.scss'
})
export class RideVerificationDetails implements OnChanges {

  private carService = inject(CarService);
  private customerService = inject(CustomerService);
  private router = inject(Router);
  carDetails = signal<Car | null>(null);
  customerDetails = signal<Customer | null>(null);

  @Input() ride: Ride;
  @Input() photos: EndRidePhotos;
  @Input() visible = false;
  @Input() currentIndex = 0;
  @Input() total = 0;

  @Input() activeTab: string | null = 'PENDING';
  @Input() showNav = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() approve = new EventEmitter<void>();
  @Output() reject = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();
  @Output() tabChange = new EventEmitter<string | null>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ride'] && this.ride) {
      if (this.ride.carId) {
        this.carDetails.set(null);
        this.carService.getCarById(String(this.ride.carId)).subscribe({
          next: (res) => this.carDetails.set(res.data),
        });
      }
      if (this.ride.customerId) {
        this.customerDetails.set(null);
        this.customerService.getCustomerById(String(this.ride.customerId)).subscribe({
          next: (res) => this.customerDetails.set(res.data),
        });
      }
    }
  }


  get isPending(): boolean {
    return this.photos?.status === 'PENDING';
  }

  get hasPrev(): boolean {
    return this.currentIndex > 0;
  }

  get hasNext(): boolean {
    return this.currentIndex < this.total - 1;
  }

  // ── Lightbox ──────────────────────────────────────────────────
  lightboxOpen = false;
  lightboxIndex = 0;

  get allPhotos(): PhotoEntry[] {
    const p = this.photos;
    if (!p) return [];
    return [
      { label: 'Parking Board', url: p.parkingBoardPhotoUrl },
    ].filter(ph => !!ph.url);
  }

  openLightbox(label: string): void {
    const idx = this.allPhotos.findIndex(p => p.label === label);
    this.lightboxIndex = idx >= 0 ? idx : 0;
    this.lightboxOpen = true;
  }

  lightboxPrev(): void {
    if (this.lightboxIndex > 0) this.lightboxIndex--;
  }

  lightboxNext(): void {
    if (this.lightboxIndex < this.allPhotos.length - 1) this.lightboxIndex++;
  }

  get lightboxPhoto(): PhotoEntry | null {
    return this.allPhotos[this.lightboxIndex] ?? null;
  }

  get parkingPhoto(): { label: string; url: string } | null {
    const url = this.photos?.parkingBoardPhotoUrl;
    return url ? { label: 'Parking Board', url } : { label: 'Parking Board', url: '' };
  }

  get customerInitials(): string {
    const f = (this.ride?.customerFirstName ?? '').trim()[0] ?? '';
    const l = (this.ride?.customerLastName ?? '').trim()[0] ?? '';
    return (f + l).toUpperCase() || '?';
  }

  get statusClass(): string {
    switch (this.photos?.status) {
      case 'APPROVED': return 'status--approved';
      case 'REJECTED': return 'status--rejected';
      default: return 'status--pending';
    }
  }

  close() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onApprove()               { this.approve.emit(); }
  onReject()                { this.reject.emit(); }
  onPrev()                  { this.prev.emit(); }
  onNext()                  { this.next.emit(); }
  onTabChange(tab: string | null) { this.tabChange.emit(tab); }

  redirectToRideDetails(): void {
    this.router.navigate([`/rides/${this.ride.rideId}`]);
  }

  redirectToVehicleDetails(): void {
    if (this.ride.carId) {
      this.router.navigate([`/vehicles/${this.ride.carId}`]);
    }
  }

  redirectToCustomerDetails(): void {
    if (this.ride.customerId) {
      this.router.navigate([`/customers/${this.ride.customerId}`]);
    }
  }

  onRideClick(event: MouseEvent) {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    this.redirectToRideDetails();
  }

  onVehicleClick(event: MouseEvent) {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    this.redirectToVehicleDetails();
  }

  onCustomerClick(event: MouseEvent) {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    this.redirectToCustomerDetails();
  }

  openEndLocation(): void {
    const lat = this.ride.telemetryEndLat;
    const lng = this.ride.telemetryEndLng;

    if (lat && lng) {
      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      window.open(url, '_blank');
    }
  }
}
